import React, { useEffect, useMemo, useState } from "react";
import SEO from "@/components/SEO";
import { toast } from "sonner";
import { Search, ShoppingBag, X, Lock, Calendar, Mail, User, Coins, Image as ImageIcon, ShieldCheck } from "lucide-react";
import { adminApi, formatErr } from "@/admin/api";
import { Panel, AdminInput, EmptyState } from "@/admin/components/ui";

const fmtDate = (iso) => { try { return new Date(iso).toLocaleString("en-ZA", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; } };

const DetailDrawer = ({ purchaseId, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    adminApi.get(`/admin/purchases/${purchaseId}`)
      .then((r) => { if (active) setData(r.data); })
      .catch((err) => toast.error(formatErr(err)))
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [purchaseId]);

  return (
    <>
      <div className="fixed inset-0 bg-black/75 backdrop-blur-sm z-50" onClick={onClose} data-testid="purchase-detail-overlay" />
      <aside data-testid="purchase-detail-drawer" className="fixed top-0 right-0 bottom-0 w-full sm:w-[640px] z-50 glass-strong border-l border-white/10 overflow-y-auto">
        <header className="px-5 sm:px-6 py-4 border-b border-white/5 flex items-center gap-3 sticky top-0 glass-strong">
          <ShoppingBag className="w-4 h-4 text-[#ff2e93]" />
          <h2 className="font-display text-lg text-chrome flex-1 min-w-0 truncate">Purchase Detail</h2>
          <button onClick={onClose} data-testid="purchase-detail-close" className="w-8 h-8 rounded-full border border-white/10 text-white/70 hover:text-white flex items-center justify-center"><X className="w-4 h-4" /></button>
        </header>
        <div className="px-5 sm:px-6 py-5 space-y-5">
          {loading || !data ? <p className="text-sm text-white/40">Loading…</p> : (
            <>
              <Panel testId="purchase-detail-customer">
                <p className="text-[10px] tracking-luxe uppercase text-white/45">Customer</p>
                <p className="font-display text-lg text-chrome mt-1">{data.customer?.name} {data.customer?.surname}</p>
                <p className="text-xs text-white/55 inline-flex items-center gap-1 mt-1"><Mail className="w-3 h-3" /> {data.customer?.email}</p>
                <p className="text-[10px] tracking-luxe uppercase text-white/45 mt-3 inline-flex items-center gap-1"><Coins className="w-3 h-3" /> Current balance: {data.customer?.credit_balance ?? 0} cr</p>
              </Panel>

              <Panel testId="purchase-detail-summary" title="Summary">
                <div className="grid grid-cols-2 gap-3">
                  <div className="glass chrome-border rounded-md p-3"><p className="text-[10px] tracking-luxe uppercase text-white/45">Purchase ID</p><p className="text-xs font-mono text-white">{data.purchase.id}</p></div>
                  <div className="glass chrome-border rounded-md p-3"><p className="text-[10px] tracking-luxe uppercase text-white/45">Date</p><p className="text-xs text-white">{fmtDate(data.purchase.created_at)}</p></div>
                  <div className="glass chrome-border rounded-md p-3"><p className="text-[10px] tracking-luxe uppercase text-white/45">Photos</p><p className="font-display text-2xl text-chrome">{data.purchase.total_photos}</p></div>
                  <div className="glass chrome-border rounded-md p-3"><p className="text-[10px] tracking-luxe uppercase text-white/45">Credits Used</p><p className="font-display text-2xl text-[#ff7fc1]">{data.purchase.total_credits_used}</p></div>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <span data-testid="purchase-detail-status" className="text-[10px] tracking-luxe uppercase px-2.5 py-1 rounded-full glass-strong border border-[#7fe6ff]/40 text-[#7fe6ff]">{data.purchase.status}</span>
                  <span data-testid="purchase-detail-download-status" className="text-[10px] tracking-luxe uppercase px-2.5 py-1 rounded-full glass-strong border border-white/15 text-white/55 inline-flex items-center gap-1">
                    <Lock className="w-3 h-3" /> {(data.purchase.download_status || "").replace(/_/g, " ") || "locked"}
                  </span>
                </div>
              </Panel>

              {data.purchase.pricing_breakdown?.length > 0 && (
                <Panel testId="purchase-detail-breakdown" title="Pricing Breakdown">
                  {data.purchase.pricing_breakdown.map((g, i) => (
                    <div key={i} className="border-t first:border-t-0 border-white/5 py-3 first:pt-0">
                      <p className="font-display text-sm text-chrome">{g.event_title}</p>
                      <p className="text-[10px] tracking-luxe uppercase text-white/45">{g.selected_count} photo{g.selected_count === 1 ? "" : "s"} · {g.total_credits} cr</p>
                      <ul className="text-xs text-white/65 mt-1 space-y-0.5">
                        {(g.breakdown || []).map((b, j) => (
                          <li key={j} className="flex justify-between"><span>{b.qty} × {b.label} @ {b.unit_credits} cr</span><span>{b.subtotal} cr</span></li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </Panel>
              )}

              {data.credit_transaction && (
                <Panel testId="purchase-detail-transaction" title="Linked Credit Transaction">
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div><p className="text-[10px] tracking-luxe uppercase text-white/45">Type</p><p className="text-white">{data.credit_transaction.type}</p></div>
                    <div><p className="text-[10px] tracking-luxe uppercase text-white/45">Amount</p><p className="text-[#ff7fc1] font-display text-lg">-{data.credit_transaction.amount}</p></div>
                    <div><p className="text-[10px] tracking-luxe uppercase text-white/45">Balance Before</p><p className="text-white">{data.credit_transaction.balance_before}</p></div>
                    <div><p className="text-[10px] tracking-luxe uppercase text-white/45">Balance After</p><p className="text-white">{data.credit_transaction.balance_after}</p></div>
                  </div>
                </Panel>
              )}

              <Panel testId="purchase-detail-photos" title={`Photos (${data.photos.length})`} className="!p-0 overflow-hidden">
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 p-3">
                  {data.photos.map((ph) => (
                    <div key={ph.id} data-testid={`purchase-detail-photo-${ph.id}`} className="relative photo-frame chrome-border aspect-square overflow-hidden">
                      {ph.thumbnail_url
                        ? <img src={ph.thumbnail_url} alt={ph.display_name} loading="lazy" className="w-full h-full object-cover" />
                        : <div className="w-full h-full bg-black/60 flex items-center justify-center text-white/30"><ImageIcon className="w-5 h-5" /></div>}
                      <span className="absolute bottom-1 left-1 right-1 text-[9px] tracking-luxe uppercase text-white truncate bg-black/55 rounded px-1 py-0.5">{ph.display_name}</span>
                    </div>
                  ))}
                </div>
                <div className="px-4 py-3 border-t border-white/5 text-[10px] tracking-luxe uppercase text-white/45 flex items-center gap-2">
                  <ShieldCheck className="w-3.5 h-3.5 text-[#ff2e93]" />
                  Internal R2 keys retained for Phase 7 secure-download activation. Never exposed to customers.
                </div>
              </Panel>
            </>
          )}
        </div>
      </aside>
    </>
  );
};

export default function AdminPurchases() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [active, setActive] = useState(null);

  useEffect(() => {
    setLoading(true);
    adminApi.get("/admin/purchases")
      .then((r) => setRows(r.data))
      .catch((err) => toast.error(formatErr(err)))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => rows.filter((r) => {
    if (!q) return true;
    const blob = `${r.customer_email} ${r.id} ${r.total_credits_used}`.toLowerCase();
    return blob.includes(q.toLowerCase());
  }), [rows, q]);

  const totals = useMemo(() => {
    return {
      count: filtered.length,
      credits: filtered.reduce((s, r) => s + (r.total_credits_used || 0), 0),
      photos: filtered.reduce((s, r) => s + (r.total_photos || 0), 0),
    };
  }, [filtered]);

  return (
    <div data-testid="page-admin-purchases" className="space-y-6">
      <SEO title="AdminPurchases · WelkomBiz Photography" noindex />
      <Panel testId="purchases-explainer">
        <div className="flex items-start gap-3">
          <ShoppingBag className="w-4 h-4 text-[#ff2e93] mt-0.5 shrink-0" />
          <p className="text-sm text-white/65 font-body">
            Customer purchases made with credits. Originals are <strong>not exposed to customers</strong> — only internal R2
            keys are retained for Phase 7 secure-download activation. Click a row to view the full breakdown including the linked credit transaction.
          </p>
        </div>
      </Panel>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex items-center gap-3 flex-1 glass chrome-border rounded-full px-4 py-2">
          <Search className="w-4 h-4 text-white/45" />
          <input data-testid="purchases-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by customer email, purchase ID…" className="flex-1 bg-transparent text-sm text-white placeholder-white/35 outline-none font-body" />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div data-testid="purchases-total-count" className="glass chrome-border rounded-md p-3"><p className="text-[10px] tracking-luxe uppercase text-white/45">Purchases</p><p className="font-display text-2xl text-chrome">{totals.count}</p></div>
        <div data-testid="purchases-total-photos" className="glass chrome-border rounded-md p-3"><p className="text-[10px] tracking-luxe uppercase text-white/45">Photos Sold</p><p className="font-display text-2xl text-[#7fe6ff]">{totals.photos}</p></div>
        <div data-testid="purchases-total-credits" className="glass chrome-border rounded-md p-3"><p className="text-[10px] tracking-luxe uppercase text-white/45">Credits Used</p><p className="font-display text-2xl text-[#ff7fc1]">{totals.credits}</p></div>
      </div>

      <Panel testId="purchases-table" className="!p-0 overflow-hidden">
        {loading ? <p className="p-6 text-sm text-white/40">Loading…</p> :
         filtered.length === 0 ? <div className="p-6"><EmptyState title="No purchases yet" message="Once customers confirm credit purchases, the records will appear here." /></div> :
         (<>
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/3 text-[10px] tracking-luxe uppercase text-white/45">
                <tr>
                  <th className="text-left px-5 py-3">Date</th>
                  <th className="text-left px-5 py-3">Customer</th>
                  <th className="text-left px-5 py-3">Photos</th>
                  <th className="text-left px-5 py-3">Credits</th>
                  <th className="text-left px-5 py-3">Status</th>
                  <th className="text-left px-5 py-3">Download</th>
                  <th className="text-right px-5 py-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} data-testid={`purchase-row-${r.id}`} className="border-t border-white/5 hover:bg-white/3">
                    <td className="px-5 py-3 text-white/70 whitespace-nowrap">{fmtDate(r.created_at)}</td>
                    <td className="px-5 py-3 text-white/85 truncate max-w-[220px]">{r.customer_email}</td>
                    <td className="px-5 py-3 text-white">{r.total_photos}</td>
                    <td className="px-5 py-3 text-[#ff7fc1] font-display">{r.total_credits_used}</td>
                    <td className="px-5 py-3"><span className="text-[10px] tracking-luxe uppercase text-[#7fe6ff]">{r.status}</span></td>
                    <td className="px-5 py-3"><span className="text-[10px] tracking-luxe uppercase text-white/55 inline-flex items-center gap-1"><Lock className="w-3 h-3" /> {(r.download_status || "").replace(/_/g, " ") || "locked"}</span></td>
                    <td className="px-5 py-3 text-right">
                      <button onClick={() => setActive(r.id)} data-testid={`purchase-view-${r.id}`} className="text-[10px] tracking-luxe uppercase text-white/70 hover:text-white">View →</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="md:hidden divide-y divide-white/5">
            {filtered.map((r) => (
              <button key={r.id} data-testid={`purchase-card-${r.id}`} onClick={() => setActive(r.id)} className="block w-full text-left p-4">
                <p className="text-sm text-white truncate">{r.customer_email}</p>
                <p className="text-[10px] tracking-luxe uppercase text-white/45 mt-0.5">{fmtDate(r.created_at)}</p>
                <p className="font-display text-lg text-chrome mt-1">{r.total_photos} photos · {r.total_credits_used} cr</p>
              </button>
            ))}
          </div>
         </>)}
      </Panel>

      {active && <DetailDrawer purchaseId={active} onClose={() => setActive(null)} />}
    </div>
  );
}
